/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: EmailTemplatesFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the email-templates module.
----------------------------------------------------------------*/

import React, { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useNotifications } from '@/contexts/NotificationContext';
import { ModuleFormHeader } from '@/components/layouts';
import FixedActionBar from '@/components/ui/FixedActionBar';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import { useNavigationStore } from '@/store/navigation';
import { useEmailTemplateData } from './hooks/useEmailTemplateData';
import { useEmailTemplateForm } from './hooks/useEmailTemplateForm';
import { useEmailTemplateSubmit } from './hooks/useEmailTemplateSubmit';
import { TemplateFormFields } from './components/TemplateFormFields';
import { useSaveWithAction } from '@/hooks/useSaveWithAction';
import type { EmailTemplate } from './api/emailTemplatesApi';

export default function EmailTemplatesFormView() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { notifications, removeNotification } = useNotifications();
    const { setCurrentPageTitle } = useNavigationStore();

    const duplicateId = searchParams.get('duplicate');
    const isEditMode = Boolean(id && id !== 'new' && !duplicateId);
    const mode = id === 'new' || duplicateId ? 'create' : 'edit';


    const { formData, setFormData, baseline, setBaseline, isLoading } = useEmailTemplateData(id, duplicateId);

    const { errors, hasChanges, validateForm, handleInputChange } = useEmailTemplateForm(
        formData,
        baseline,
        'edit',
        setFormData
    );

    const {
        isSaving,
        submitTemplate,
        handleCancel,
    } = useEmailTemplateSubmit(
        formData,
        baseline,
        hasChanges,
        validateForm,
        'edit',
        id,
        setFormData,
        setBaseline
    );

    const handleSaveInternal = useCallback(async (): Promise<EmailTemplate | undefined> => {
        return await submitTemplate();
    }, [submitTemplate]);

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/admin/system-settings/email-template',
        newPath: '/admin/system-settings/email-template', // Redirect back to list
        mode: 'edit',
        getEditPath: (savedData: EmailTemplate) => `/admin/system-settings/email-template/${savedData.id}`
    });

    const handleBack = () => {
        navigate('/admin/system-settings/email-template');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<Mail className="h-5 w-5 text-primary" />}
                title={isEditMode ? `${t('emailTemplates.actions.edit')} - ${formData.name || ''}` : t('emailTemplates.actions.create')}
                onBack={handleBack}
                backButtonText={t('emailTemplates.actions.list')}
            />

            <div className="mt-8">
                <form onSubmit={(e) => { e.preventDefault(); handleSaveWithAction('save'); }}>
                    <TemplateFormFields
                        formData={formData}
                        errors={errors}
                        onInputChange={handleInputChange}
                        mode="edit"
                    />
                </form>
            </div>

            <FixedActionBar
                onSaveWithAction={handleSaveWithAction}
                onCancel={handleCancel}
                isEditMode={isEditMode}
                isSaving={isSaving}
                cancelText={t('common.cancel')}
                useSaveDropdown={true}
                saveDropdownStorageKey="emailTemplates:saveAction"
                availableSaveActions={['save', 'save-and-close']}
                saveDisabled={isSaving}
            />
        </div>
    );
}
